import { Button, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import GlobalCanvas from 'utils/canvas'

interface Props {
  imgPath?: string
  positions?: {
    x: number
    y: number
    size: number
  }[]
  addPosition: (positions: { x: number; y: number; size: number }) => void
  clearPosition: () => void
  undoPosition: () => void
  handleFinish: (val: string) => void
}
type State = {
  rate: number
  x: number
  y: number
  size: number
  image?: CanvasImageSource
}
const defaultState: State = {
  x: 0,
  y: 0,
  size: 20,
  rate: 1,
}
export default function CanvasArea({
  imgPath = '',
  positions = [],
  addPosition,
  clearPosition,
  undoPosition,
  handleFinish,
}: Props) {
  const canvasRef = useRef(null as any as HTMLCanvasElement)
  const [state, update] = useState<State>({ ...defaultState })
  const globalCanvas = useMemo(() => {
    if (canvasRef.current) return new GlobalCanvas(canvasRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef.current])
  const [clientSize, setClientSize] = useState(0)

  useEffect(() => {
    const setSize = () => {
      setClientSize(window.innerWidth)
    }
    setSize()
    if (clientSize === 0) {
      window.addEventListener('resize', setSize)
    }
    return () => {
      window.removeEventListener('resize', setSize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (clientSize > 1024) {
      update((pre) => ({
        ...pre,
        rate: 2,
      }))
    } else if (clientSize > 600) {
      update((pre) => ({
        ...pre,
        rate: 1.5,
      }))
    } else if (clientSize > 400) {
      update((pre) => ({
        ...pre,
        rate: 1,
      }))
    } else {
      update((pre) => ({
        ...pre,
        rate: 0.8,
      }))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSize])

  const resetCanvas = () => {
    if (globalCanvas && imgPath !== '') {
      const img = new Image()
      img.src = imgPath
      img.onload = () => {
        globalCanvas.clearRect()
        globalCanvas.drawImageElm(img, state.rate)
        globalCanvas.drawHistory(positions, state.rate)
        if (state.x !== 0 && state.y !== 0) {
          globalCanvas.drawBox(state.x, state.y, state.size * state.rate)
        } else {
          handleFinish(globalCanvas.toString())
        }
      }
    }
  }

  useEffect(() => {
    resetCanvas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCanvas, imgPath, state.rate, state.size, state.x, state.y])

  useEffect(() => {
    resetCanvas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions])

  const handleCanvasOnClick = (event: any) => {
    let clientX = 0,
      clientY = 0
    if (event.clientX) {
      clientX = event.clientX
      clientY = event.clientY
    } else if (event.touches[0].clientX) {
      clientX = event.touches[0].clientX
      clientY = event.touches[0].clientY
    } else return
    if (!globalCanvas) return
    const clientRect = event.target.getBoundingClientRect()
    const x = clientX - clientRect.left
    const y = clientY - clientRect.top
    update({
      ...state,
      x,
      y,
    })
  }

  return (
    <>
      <Typography variant="body1" width={'100%'}>
        空欄のサイズ
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2} my={2}>
        <Button
          size="small"
          variant={'outlined'}
          onClick={() => {
            update({
              ...state,
              size: state.size - 5,
            })
          }}
        >
          - 5
        </Button>
        <Button
          size="small"
          variant={'outlined'}
          onClick={() => {
            update({
              ...state,
              size: state.size - 1,
            })
          }}
        >
          - 1
        </Button>
        <Typography>{state.size}</Typography>
        <Button
          size="small"
          variant={'outlined'}
          onClick={() => {
            update({
              ...state,
              size: state.size + 1,
            })
          }}
        >
          + 1
        </Button>
        <Button
          size="small"
          variant={'outlined'}
          onClick={() => {
            update({
              ...state,
              size: state.size + 5,
            })
          }}
        >
          + 5
        </Button>
      </Stack>
      <canvas ref={canvasRef} onClick={handleCanvasOnClick}></canvas>
      <Stack
        direction={{
          md: 'row',
          xs: 'column-reverse',
        }}
        spacing={2}
        mt={2}
      >
        <Stack direction="row" spacing={2}>
          <Button
            size="small"
            disabled={positions.length === 0}
            variant="contained"
            color="secondary"
            onClick={() => {
              clearPosition()
              update({
                ...state,
                x: 0,
                y: 0,
              })
            }}
          >
            Clear
          </Button>
          <Button
            size="small"
            disabled={positions.length === 0}
            variant="contained"
            color="secondary"
            onClick={() => {
              undoPosition()
              update({
                ...state,
                x: 0,
                y: 0,
              })
            }}
          >
            undo
          </Button>
          <Button
            size="small"
            disabled={state.x === 0}
            variant="contained"
            onClick={() => {
              const { rate, size, x, y } = state
              addPosition({
                size,
                x: x / rate,
                y: y / rate,
              })
              update({
                ...state,
                x: 0,
                y: 0,
              })
            }}
          >
            Next
          </Button>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <input
            value={state.x}
            type="number"
            style={{ width: 80 }}
            onChange={(e) => {
              update({
                ...state,
                x: Number(e.target.value),
              })
            }}
          />
          <Typography>X</Typography>
          <input
            value={state.y}
            type="number"
            style={{ width: 80 }}
            onChange={(e) => {
              update({
                ...state,
                y: Number(e.target.value),
              })
            }}
          />
        </Stack>
      </Stack>
    </>
  )
}
